/**
 * Sensbey AIアシスタント — private per-user assistant conversation panel.
 *
 * Lives inside the Chat app's main content area but is NOT a channel:
 * conversations use the dedicated /api/assistant/* backend (type
 * assistant_conversation) and are visible only to their owner.
 * Read-only Phase 1 — the assistant retrieves module data through
 * permission-aware backend tools and never performs write actions.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Sparkle, PaperPlaneTilt, Stop, Plus, ShareNetwork, Paperclip,
  ClipboardText, ArrowsClockwise, CaretLeft, ArrowSquareOut, X,
  ThumbsUp, ThumbsDown, MagnifyingGlass, CircleNotch,
  BookmarkSimple, Lightbulb, Lightning, Check, Warning,
  Download, Trash, FilePdf, FileDoc, FileXls, MicrosoftPowerpointLogo,
} from '@phosphor-icons/react';
import { CodeBlockRunner, splitMessageByCodeFences } from './CodeBlockRunner';
import DocGenPanel from './DocGenPanel';
import ModelPresetPicker from '@/components/ai/ModelPresetPicker';
import { formatDateTime, formatShort } from '@/utils/datetime';
import { jstDateTime } from '@/utils/jstDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 英語の生エラーをUIに出さない — 日本語ならそのまま、それ以外は汎用文言
const jaError = (m) => {
  if (m && /[\u3040-\u30ff\u4e00-\u9fff]/.test(m)) return m;
  return 'AIサービスでエラーが発生しました。時間をおいて再度お試しください。';
};

const STARTERS = [
  '最新の全社お知らせを説明して',
  '自分の進行中のプロジェクトを要約して',
  '注意が必要な製造オーダーはある？',
  '購買依頼の提出手順をナレッジで探して',
  '申請中の勤怠申請を確認して',
  '日本語と英語の翻訳を手伝って',
];

const QUICK_ACTIONS = [
  'このメッセージを説明して',
  'このメッセージを要約して',
  'このメッセージを翻訳して',
  'このメッセージから対応事項・期限を抽出して',
];

function fmtTime(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return formatShort(d);
}

function Sources({ sources, usedSelection }) {
  if (!sources?.length && !usedSelection) return null;
  return (
    <div className="mt-3 pt-3 border-t border-zinc-100" data-testid="assistant-sources">
      {sources?.length > 0 && (
        <>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-1.5 flex items-center gap-1.5">
            <ArrowSquareOut size={10} />この情報の出典 · {sources.length}件
          </p>
          <div className="flex flex-wrap gap-1.5">
            {sources.slice(0, 12).map((s, i) => (
              <a key={`${s.url}-${i}`} href={s.url} target="_blank" rel="noreferrer"
                data-testid={`assistant-source-${i}`}
                title={s.title}
                className="inline-flex items-center gap-1.5 max-w-[280px] px-2.5 py-1 rounded-full text-[11px]
                           bg-violet-50 border border-violet-200 text-violet-800
                           hover:bg-violet-100 hover:border-violet-400 hover:shadow-sm
                           active:scale-95 transition-all cursor-pointer">
                <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider opacity-70">{s.module}</span>
                <span className="truncate">{s.title || '無題'}</span>
                <ArrowSquareOut size={10} className="shrink-0 opacity-60" />
              </a>
            ))}
          </div>
        </>
      )}
      {usedSelection && (
        <p className="text-[10px] text-zinc-400 mt-1.5" data-testid="assistant-selection-note">選択したチャットメッセージを参照しました</p>
      )}
    </div>
  );
}

function AssistantMessage({ msg, onFeedback, onSaveNote, onDelete, onContinue }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(msg.text || ''); toast.success('クリップボードにコピーしました'); }
    catch { toast.error('コピーに失敗しました'); }
  };
  return (
    <div className="group flex gap-3 px-4 py-2" data-testid={`assistant-msg-${msg.id || 'stream'}`}>
      <span className="shrink-0 w-8 h-8 rounded-lg bg-teal-700 text-white flex items-center justify-center mt-0.5">
        <Sparkle size={15} weight="fill" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-zinc-700">Sensbey AIアシスタント</span>
          {msg.created_at && <span className="text-[10px] text-zinc-400">{fmtTime(msg.created_at)}</span>}
          {msg.status === 'stopped' && <span className="text-[10px] text-amber-600">（停止済み）</span>}
          {msg.status === 'incomplete' && !msg.continuing && (
            <span className="text-[10px] text-amber-600" data-testid={`assistant-incomplete-${msg.id || 'last'}`}>
              （未完了の可能性）
            </span>
          )}
          {!msg.streaming && msg.text && (
            <button type="button" onClick={copy} data-testid={`assistant-copy-${msg.id || 'last'}`}
              className="opacity-0 group-hover:opacity-100 p-1 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-opacity" title="コピー">
              <ClipboardText size={13} />
            </button>
          )}
          {!msg.streaming && msg.text && onSaveNote && msg.id && !String(msg.id).startsWith('local-') && !String(msg.id).startsWith('a-') && (
            <button type="button" onClick={() => onSaveNote(msg)} data-testid={`assistant-save-note-${msg.id}`}
              className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-violet-600 hover:bg-violet-50 transition-opacity"
              title="この回答をナレッジ下書きとして保存">
              <BookmarkSimple size={11} /> ノートに保存
            </button>
          )}
          {!msg.streaming && msg.text && (
            <ExportDropdown title={msg.text.slice(0, 60).split('\n')[0] || 'AI回答'} content={msg.text} msgId={msg.id} />
          )}
          {!msg.streaming && msg.id && onDelete && (
            <button type="button" onClick={() => onDelete(msg.id)}
              data-testid={`assistant-msg-delete-${msg.id}`}
              className="opacity-0 group-hover:opacity-100 p-1 rounded text-zinc-400 hover:text-rose-600 hover:bg-rose-50 transition-opacity"
              title="このメッセージを削除">
              <Trash size={11} />
            </button>
          )}
          {!msg.streaming && msg.text && onFeedback && <FeedbackButtons msg={msg} onFeedback={onFeedback} />}
        </div>
        <div className="text-sm text-zinc-800 break-words mt-0.5">
          {msg.trail && msg.trail.length > 0 && (
            <ProgressTrail steps={msg.trail} streaming={!!msg.streaming} />
          )}
          {(() => {
            // Extract <generated-file>...</generated-file> tags BEFORE code-fence
            // splitting so they don't collide with fenced code.
            const genFiles = [];
            const attrOf = (attrs, name) => (new RegExp(`${name}=["']([^"']+)["']`).exec(attrs || '') || [])[1] || '';
            const pushGen = (attrs, content) => {
              genFiles.push({
                format: (attrOf(attrs, 'format') || 'docx').trim(),
                title: (attrOf(attrs, 'title') || 'document').trim(),
                content: content.trim(),
              });
              return `\n[__GEN_FILE_${genFiles.length - 1}__]\n`;
            };
            let cleaned = (msg.text || '').replace(
              /<generated-file\b([^>]*)>([\s\S]*?)<\/generated-file>/g,
              (_m, attrs, content) => pushGen(attrs, content),
            );
            // Fallback: unclosed tag (model forgot </generated-file>) — once
            // streaming ends, treat everything after the opening tag as body.
            if (!msg.streaming) {
              cleaned = cleaned.replace(
                /<generated-file\b([^>]*)>([\s\S]*)$/,
                (_m, attrs, content) => (content.trim() ? pushGen(attrs, content) : ''),
              );
            }
            // Strip any stray <chart-image .../> tags (assistant no longer emits
            // these — remove silently if a stale prompt slips through).
            cleaned = cleaned.replace(/<chart-image\s+[^>]*\/?>(?:<\/chart-image>)?/g, '');
            const parts = splitMessageByCodeFences(cleaned);
            const rendered = [];
            parts.forEach((p, i) => {
              if (p.type === 'code') { rendered.push(<CodeBlockRunner key={`c${i}`} code={p.content} lang={p.lang} />); return; }
              const text = p.content;
              const tokens = [...text.matchAll(/\[__GEN_FILE_(\d+)__\]/g)];
              if (!tokens.length) { rendered.push(<MarkdownBlock key={`t${i}`} text={text} />); return; }
              let last = 0;
              tokens.forEach((tok, k) => {
                const idx = tok.index;
                if (idx > last) rendered.push(<MarkdownBlock key={`t${i}-p${k}`} text={text.slice(last, idx)} />);
                rendered.push(<GeneratedFile key={`gf${i}-${k}`} spec={genFiles[Number(tok[1])]} />);
                last = idx + tok[0].length;
              });
              if (last < text.length) rendered.push(<MarkdownBlock key={`t${i}-end`} text={text.slice(last)} />);
            });
            return rendered;
          })()}
          {msg.streaming && !msg.text && (!msg.trail || msg.trail.length === 0) && <ThinkingIndicator stage={msg.stage} />}
          {msg.streaming && msg.text && <span className="inline-block w-1.5 h-4 ml-0.5 bg-violet-500 animate-pulse rounded-sm align-text-bottom" />}
          {!msg.streaming && msg.text && msg.export_hints?.length > 0 && (
            <QuickExportBar hints={msg.export_hints} title={firstLineOf(msg.text) || 'AI回答'} content={msg.text} />
          )}
          {/* 続きを生成 — トークン上限で途切れた/停止した回答を正確な位置から再開 */}
          {!msg.streaming && msg.text && onContinue
            && (msg.status === 'incomplete' || msg.status === 'stopped') && (
            <div className="mt-2.5">
              {msg.status === 'incomplete' && !msg.continuing && (
                <p className="text-[11px] text-amber-700 mb-1.5">
                  回答が完了する前に中断された可能性があります。最後の位置から続きを生成できます。
                </p>
              )}
              <button type="button" onClick={() => onContinue(msg)} disabled={!!msg.continuing}
                data-testid={`assistant-continue-${msg.id || 'last'}`}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold
                           bg-violet-600 text-white hover:bg-violet-700 active:scale-95 transition-all
                           disabled:opacity-60 disabled:cursor-not-allowed shadow-sm">
                {msg.continuing
                  ? <CircleNotch size={13} className="animate-spin" />
                  : <Sparkle size={13} weight="fill" />}
                {msg.continuing ? '続きを生成中…' : '続きを生成'}
              </button>
            </div>
          )}
        </div>
        {!msg.streaming && <Sources sources={msg.sources} usedSelection={msg.used_selection} />}
        {!msg.streaming && (msg.model_id || msg.preset_name) && (
          <p className="mt-0.5 text-[9px] text-zinc-300 font-mono truncate select-none"
            title={`このメッセージを生成したモデル: ${msg.model_id || '既定'}${msg.preset_name ? ` / プリセット: ${msg.preset_name}` : ''}`}
            data-testid={`assistant-msg-model-${msg.id}`}>
            {msg.model_id || ''}{msg.preset_name ? ` · ${msg.preset_name}` : ''}
          </p>
        )}
      </div>
    </div>
  );
}

// Lightweight markdown renderer — Tailwind-scoped so it inherits our type scale.
const _MD_LINK = ({ node, ...props }) => (
  <a {...props} target="_blank" rel="noopener noreferrer" />
);
const _MD_TABLE = ({ node, ...props }) => (
  <div className="my-2 overflow-x-auto rounded-lg border border-zinc-200">
    <table className="min-w-full text-[13px] border-collapse" {...props} />
  </div>
);
const _MD_TH = ({ node, ...props }) => (
  <th className="bg-zinc-50 border-b border-zinc-200 px-3 py-1.5 text-left font-semibold text-zinc-800" {...props} />
);
const _MD_TD = ({ node, ...props }) => (
  <td className="border-b border-zinc-100 px-3 py-1.5 align-top text-zinc-700" {...props} />
);
const _MD_COMPONENTS = { a: _MD_LINK, table: _MD_TABLE, th: _MD_TH, td: _MD_TD };
function MarkdownBlock({ text }) {
  if (!text) return null;
  return (
    <div className="prose prose-sm prose-zinc max-w-none
                    prose-p:my-1.5 prose-p:leading-relaxed
                    prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5
                    prose-strong:text-zinc-900 prose-strong:font-semibold
                    prose-code:bg-zinc-100 prose-code:text-violet-700 prose-code:px-1
                    prose-code:py-0.5 prose-code:rounded prose-code:text-[0.85em]
                    prose-code:before:content-none prose-code:after:content-none
                    prose-a:text-violet-600 prose-a:no-underline hover:prose-a:underline
                    prose-h1:text-base prose-h2:text-base prose-h3:text-sm prose-h4:text-sm
                    prose-h1:mt-3 prose-h2:mt-3 prose-h3:mt-2 prose-h4:mt-2
                    prose-blockquote:border-l-violet-300 prose-blockquote:text-zinc-600
                    prose-hr:my-3">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={_MD_COMPONENTS}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

// Grok-style vertical timeline: shows every step the AI took while thinking.
function ProgressTrail({ steps, streaming }) {
  const [collapsed, setCollapsed] = useState(!streaming);
  useEffect(() => { if (streaming) setCollapsed(false); }, [streaming]);
  if (!steps?.length) return null;
  const STAGE_META = {
    routing:    { emoji: '🧭', label: '意図を判定中',   done_label: '意図を判定'   },
    tool:       { emoji: '🔎', label: '検索中',        done_label: '検索'        },
    tool_done:  { emoji: '✅', label: '検索完了',      done_label: '検索完了'    },
    tool_error: { emoji: '⚠️', label: '検索失敗',      done_label: '検索失敗'    },
    generating: { emoji: '✍️', label: '回答を作成中', done_label: '回答作成'    },
    chunking:   { emoji: '📄', label: '資料を分割処理中', done_label: '資料を分割処理' },
    continuing: { emoji: '⏩', label: '続きを生成中',  done_label: '続きを生成'  },
    continue_failed: { emoji: '⚠️', label: '継続が中断されました', done_label: '継続が中断' },
    incomplete: { emoji: '⚠️', label: '未完了の可能性', done_label: '未完了の可能性' },
  };
  const summaryLast = steps[steps.length - 1];
  const summaryMeta = STAGE_META[summaryLast?.stage] || { emoji: '⚙️', label: summaryLast?.stage };
  const totalCounts = steps.filter((s) => s.stage === 'tool_done').reduce((acc, s) => acc + (s.count || 0), 0);
  return (
    <div className="mb-2 rounded-xl border border-violet-100 bg-white overflow-hidden"
         data-testid="assistant-progress-trail">
      <button type="button" onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-violet-50/70 transition-colors text-left"
        data-testid="assistant-progress-toggle">
        {streaming
          ? <CircleNotch size={12} className="animate-spin text-violet-500 shrink-0" />
          : <Check size={12} weight="bold" className="text-emerald-500 shrink-0" />}
        <span className="text-[11px] font-semibold text-violet-800">
          {streaming ? summaryMeta.label : '思考プロセス'}
        </span>
        <span className="text-[10px] text-zinc-400">
          {steps.length}ステップ{totalCounts ? ` ・ ${totalCounts}件参照` : ''}
        </span>
        <span className="ml-auto text-[10px] text-violet-500">{collapsed ? '展開' : '折りたたむ'}</span>
      </button>
      {!collapsed && (
        <ol className="px-3 pb-2 pt-0.5 space-y-0.5 border-t border-violet-100/70">
          {steps.map((s, i) => {
            const meta = STAGE_META[s.stage] || { emoji: '⚙️', label: s.stage };
            const isLast = i === steps.length - 1;
            const isRunning = streaming && isLast && !['tool_done', 'tool_error', 'generating'].includes(s.stage);
            return (
              <li key={i} className="flex items-start gap-2 py-0.5" data-testid={`assistant-progress-step-${s.stage}`}>
                <span className="pt-0.5 text-[11px] leading-none w-4 text-center shrink-0">
                  {isRunning ? <CircleNotch size={10} className="animate-spin text-violet-500" />
                             : s.stage === 'tool_error' ? <Warning size={10} weight="fill" className="text-amber-500" />
                             : meta.emoji}
                </span>
                <div className="min-w-0 flex-1">
                  <span className="text-[11px] text-zinc-700">{meta.done_label || meta.label}</span>
                  {s.label && <span className="ml-1 text-[10px] text-violet-600 font-medium">— {s.label}</span>}
                  {typeof s.count === 'number' && (
                    <span className="ml-1 text-[10px] text-zinc-400">({s.count}件)</span>
                  )}
                  {s.query && (
                    <p className="text-[10px] text-zinc-400 truncate italic">&ldquo;{s.query}&rdquo;</p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function firstLineOf(text) {
  if (!text) return '';
  const line = (text || '').split('\n').find((l) => l.trim().length > 0) || '';
  return line.replace(/^#+\s*/, '').replace(/\*\*/g, '').slice(0, 80).trim();
}

// Auto-generates a file from a `<generated-file>` tag payload and renders it
// as a download card inline. Supports docx / pdf / pptx / xlsx / txt / zip.
function GeneratedFile({ spec }) {
  const [state, setState] = useState({ loading: true, url: null, filename: null, error: null, size: 0 });
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        let res;
        if (spec.format === 'zip') {
          // Content should be JSON manifest {filename, files:[{name, content, generate_format?, generate_title?}]}
          let manifest = { filename: spec.title || 'archive', files: [] };
          try {
            const parsed = JSON.parse(spec.content);
            if (parsed && typeof parsed === 'object') {
              manifest.filename = parsed.filename || manifest.filename;
              manifest.files = Array.isArray(parsed.files) ? parsed.files : [];
            }
          } catch {
            // Fallback: single text file inside
            manifest.files = [{ name: 'notes.txt', content: spec.content }];
          }
          res = await axios.post(`${API}/assistant/generate/zip`, manifest, { responseType: 'blob' });
        } else {
          res = await axios.post(`${API}/assistant/generate/document`,
            { title: spec.title, content: spec.content, format: spec.format },
            { responseType: 'blob' });
        }
        if (cancelled) return;
        const blob = new Blob([res.data], { type: res.headers['content-type'] });
        objectUrl = URL.createObjectURL(blob);
        const disp = res.headers?.['content-disposition'] || '';
        const m = /filename\*=UTF-8''([^;]+)/.exec(disp) || /filename="([^"]+)"/.exec(disp);
        const filename = m ? decodeURIComponent(m[1]) : `${spec.title || 'file'}.${spec.format}`;
        setState({ loading: false, url: objectUrl, filename, error: null, size: blob.size });
      } catch (e) {
        if (!cancelled) setState({ loading: false, url: null, filename: null, size: 0,
                                   error: e?.response?.data?.detail || '生成に失敗しました' });
      }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [spec.format, spec.title, spec.content]);

  const meta = KIND_META[spec.format] || KIND_META.text;
  const colorClass = {
    sky:     'bg-sky-50 border-sky-200 text-sky-800',
    rose:    'bg-rose-50 border-rose-200 text-rose-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    orange:  'bg-orange-50 border-orange-200 text-orange-800',
    zinc:    'bg-zinc-50 border-zinc-200 text-zinc-800',
  }[meta.color];

  if (state.loading) return (
    <div className={`my-2 rounded-xl border border-dashed ${colorClass} p-3 flex items-center gap-2`}
         data-testid={`assistant-generated-file-loading-${spec.format}`}>
      <CircleNotch size={13} className="animate-spin" />
      <span className="text-[11px] font-semibold">{spec.title}.{spec.format} を生成中…</span>
    </div>
  );
  if (state.error) return (
    <div className="my-2 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="assistant-generated-file-error">
      <p className="text-[11px] text-amber-800">
        <strong>{spec.title}.{spec.format}</strong> の生成に失敗: {state.error}
      </p>
    </div>
  );
  return (
    <a href={state.url} download={state.filename}
      data-testid={`assistant-generated-file-${spec.format}`}
      className={`my-2 flex items-center gap-3 rounded-xl border ${colorClass} p-3 hover:shadow-md transition-shadow cursor-pointer`}>
      <div className="w-10 h-10 rounded-lg bg-white/70 border border-current/20 flex flex-col items-center justify-center shrink-0">
        <span className="text-[10px] font-bold">{meta.label}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold truncate">{state.filename}</p>
        <p className="text-[10px] opacity-70 tabular-nums">{fmtBytes(state.size)} · タップでダウンロード</p>
      </div>
      <Download size={16} weight="bold" className="opacity-80" />
    </a>
  );
}


// Prominent format buttons for report/announcement-style AI replies.
function QuickExportBar({ hints, title, content }) {
  const [busy, setBusy] = useState(null);
  const doExport = async (fmt) => {
    if (busy) return;
    setBusy(fmt);
    try {
      const res = await axios.post(`${API}/assistant/generate/document`,
        { title, content, format: fmt }, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disp = res.headers?.['content-disposition'] || '';
      const m = /filename\*=UTF-8''([^;]+)/.exec(disp) || /filename="([^"]+)"/.exec(disp);
      a.download = m ? decodeURIComponent(m[1]) : `ai_export.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} をダウンロードしました`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'エクスポートに失敗しました');
    } finally { setBusy(null); }
  };
  const meta = {
    docx: { label: 'Wordで出力', Icon: FileDoc,                 color: 'sky',     bg: 'bg-sky-50',     border: 'border-sky-200',     text: 'text-sky-700',     hover: 'hover:bg-sky-100' },
    pdf:  { label: 'PDFで出力',  Icon: FilePdf,                 color: 'rose',    bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-700',    hover: 'hover:bg-rose-100' },
    pptx: { label: 'スライド化', Icon: MicrosoftPowerpointLogo, color: 'orange',  bg: 'bg-orange-50',  border: 'border-orange-200',  text: 'text-orange-700',  hover: 'hover:bg-orange-100' },
    xlsx: { label: 'Excelで出力',Icon: FileXls,                 color: 'emerald', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', hover: 'hover:bg-emerald-100' },
  };
  return (
    <div className="mt-3 flex flex-wrap gap-2 items-center" data-testid="assistant-quick-export">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 pr-1">クイック出力</span>
      {hints.filter((h) => meta[h]).map((h) => {
        const m = meta[h];
        const isBusy = busy === h;
        return (
          <button key={h} type="button" onClick={() => doExport(h)} disabled={isBusy}
            data-testid={`assistant-quick-export-${h}`}
            className={`inline-flex items-center gap-1.5 rounded-lg border ${m.border} ${m.bg} ${m.text} ${m.hover} px-3 py-1.5 text-[11px] font-semibold transition-colors disabled:opacity-50`}>
            {isBusy ? <CircleNotch size={12} className="animate-spin" /> : <m.Icon size={12} weight="fill" />}
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

function fmtBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const KIND_META = {
  image: { emoji: '🖼️', label: 'IMG',  color: 'sky' },
  pdf:   { emoji: '📕', label: 'PDF',  color: 'rose' },
  docx:  { emoji: '📘', label: 'DOC',  color: 'sky' },
  xlsx:  { emoji: '📊', label: 'XLS',  color: 'emerald' },
  pptx:  { emoji: '📙', label: 'PPT',  color: 'orange' },
  zip:   { emoji: '🗂️', label: 'ZIP',  color: 'zinc' },
  txt:   { emoji: '📄', label: 'TXT',  color: 'zinc' },
  text:  { emoji: '📄', label: 'TXT',  color: 'zinc' },
};

function AttachmentCard({ att, onRemove, onPreview }) {
  const meta = KIND_META[att.kind] || KIND_META.text;
  const colorMap = {
    sky:     'from-sky-100 to-sky-50 border-sky-200 text-sky-900',
    rose:    'from-rose-100 to-rose-50 border-rose-200 text-rose-900',
    emerald: 'from-emerald-100 to-emerald-50 border-emerald-200 text-emerald-900',
    orange:  'from-orange-100 to-orange-50 border-orange-200 text-orange-900',
    zinc:    'from-zinc-100 to-zinc-50 border-zinc-200 text-zinc-900',
  };
  return (
    <div className="relative group inline-flex items-center gap-2 rounded-xl border bg-white shadow-sm pl-1.5 pr-8 py-1.5 max-w-[240px]"
         data-testid={`assistant-attachment-${att.id}`}>
      {att.thumb_url && att.kind === 'image' ? (
        <img src={att.thumb_url} alt={att.filename}
             className="w-9 h-9 rounded-lg object-cover cursor-pointer"
             onClick={onPreview} />
      ) : (
        <button type="button" onClick={onPreview}
          className={`w-9 h-9 rounded-lg bg-gradient-to-br ${colorMap[meta.color]} border flex flex-col items-center justify-center leading-tight shrink-0`}>
          <span className="text-[9px] font-bold">{meta.label}</span>
        </button>
      )}
      <button type="button" onClick={onPreview} className="min-w-0 text-left flex-1">
        <p className="text-[11px] font-semibold text-zinc-800 truncate max-w-[170px]" title={att.filename}>{att.filename}</p>
        <p className="text-[10px] text-zinc-400 tabular-nums">
          {fmtBytes(att.size)} · {att.chars?.toLocaleString?.() || 0}文字
        </p>
      </button>
      <button type="button" onClick={onRemove}
        data-testid={`assistant-attachment-remove-${att.id}`}
        className="absolute top-1 right-1 rounded-full p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-rose-600 transition-colors"
        title="添付を削除">
        <X size={10} />
      </button>
    </div>
  );
}

function UploadingCard({ file, progress }) {
  const ext = (file.name.split('.').pop() || 'text').toLowerCase();
  const kind = ['png', 'jpg', 'jpeg', 'webp'].includes(ext) ? 'image'
             : ext === 'pdf' ? 'pdf'
             : ['docx', 'xlsx', 'pptx', 'zip'].includes(ext) ? ext : 'text';
  const meta = KIND_META[kind] || KIND_META.text;
  return (
    <div className="relative inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50/40 pl-1.5 pr-3 py-1.5 max-w-[240px] min-w-[200px]"
         data-testid="assistant-attachment-uploading">
      <div className={`w-9 h-9 rounded-lg bg-white border border-violet-200 flex flex-col items-center justify-center leading-tight shrink-0`}>
        <span className="text-[9px] font-bold text-violet-700">{meta.label}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold text-zinc-800 truncate" title={file.name}>{file.name}</p>
        <div className="mt-0.5 flex items-center gap-1.5">
          <div className="h-1 flex-1 rounded-full bg-violet-100 overflow-hidden">
            <div className="h-full bg-teal-700 transition-all duration-200"
                 style={{ width: `${progress}%` }}
                 data-testid="assistant-attachment-progress" />
          </div>
          <span className="text-[9px] font-semibold text-violet-700 tabular-nums w-8 text-right">{progress}%</span>
        </div>
        <p className="text-[9px] text-zinc-400 tabular-nums">
          {fmtBytes(file.size)} · アップロード中…
        </p>
      </div>
    </div>
  );
}

function AttachmentPreviewModal({ att, onClose }) {
  if (!att) return null;
  const meta = KIND_META[att.kind] || KIND_META.text;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
         onClick={onClose} data-testid="assistant-attachment-preview">
      <div className="w-full max-w-2xl max-h-[85vh] rounded-lg bg-white shadow-2xl flex flex-col overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-white border border-violet-200 flex items-center justify-center">
            <span className="text-[10px] font-bold text-violet-700">{meta.label}</span>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-bold text-zinc-800 truncate" title={att.filename}>{att.filename}</h3>
            <p className="text-[11px] text-zinc-400">{fmtBytes(att.size)} · {att.chars?.toLocaleString?.()}文字を読み取り</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700">
            <X size={14} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {att.kind === 'image' && att.thumb_url && (
            <img src={att.thumb_url} alt={att.filename} className="max-w-full rounded-lg mx-auto" />
          )}
          {att.kind === 'pdf' && att.thumb_url && (
            <div className="mb-3 text-center">
              <img src={att.thumb_url} alt="PDF preview" className="inline-block max-h-64 rounded-lg border border-zinc-200 shadow-sm" />
              <p className="mt-1 text-[10px] text-zinc-400">1ページ目プレビュー</p>
            </div>
          )}
          <div className="rounded-lg bg-zinc-50 border border-zinc-100 p-3 max-h-96 overflow-y-auto">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-1">読み取り内容（先頭）</p>
            <pre className="text-[11px] text-zinc-700 whitespace-pre-wrap font-sans">{att.preview || '（プレビューなし）'}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExportDropdown({ title, content, msgId }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const doExport = async (fmt) => {
    if (busy) return;
    setBusy(fmt);
    setOpen(false);
    try {
      const res = await axios.post(`${API}/assistant/generate/document`,
        { title, content, format: fmt }, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disp = res.headers?.['content-disposition'] || '';
      const m = /filename\*=UTF-8''([^;]+)/.exec(disp) || /filename="([^"]+)"/.exec(disp);
      a.download = m ? decodeURIComponent(m[1]) : `ai_export.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} をダウンロードしました`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'エクスポートに失敗しました');
    } finally { setBusy(null); }
  };
  const items = [
    { fmt: 'docx', label: 'Word (.docx)',       Icon: FileDoc,                    color: 'text-sky-600' },
    { fmt: 'xlsx', label: 'Excel (.xlsx)',      Icon: FileXls,                    color: 'text-emerald-600' },
    { fmt: 'pptx', label: 'PowerPoint (.pptx)', Icon: MicrosoftPowerpointLogo,    color: 'text-orange-600' },
    { fmt: 'pdf',  label: 'PDF (.pdf)',         Icon: FilePdf,                    color: 'text-rose-600' },
    { fmt: 'txt',  label: 'テキスト (.txt)',    Icon: ClipboardText,              color: 'text-zinc-600' },
  ];
  return (
    <div className="relative opacity-0 group-hover:opacity-100 transition-opacity">
      <button type="button" onClick={() => setOpen((v) => !v)}
        data-testid={`assistant-export-${msgId || 'last'}`}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-zinc-500 hover:text-violet-700 hover:bg-violet-50"
        title="この回答を書類として出力">
        {busy ? <CircleNotch size={11} className="animate-spin" /> : <Download size={11} />}
        エクスポート
      </button>
      {open && (
        <div className="absolute z-30 mt-1 right-0 w-44 rounded-xl border border-zinc-200 bg-white shadow-lg p-1"
             onMouseLeave={() => setOpen(false)}>
          {items.map((it) => (
            <button key={it.fmt} type="button" onClick={() => doExport(it.fmt)}
              data-testid={`assistant-export-${it.fmt}-${msgId || 'last'}`}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-[11px] text-zinc-700 hover:bg-zinc-50">
              <it.Icon size={13} weight="fill" className={it.color} />
              <span>{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ThinkingIndicator({ stage }) {
  const label = stage?.stage === 'tool'
    ? `${stage.label || 'データ'}を検索中…`
    : stage?.stage === 'chunking' ? `資料を分割処理中…（${stage.label || ''}）`
    : stage?.stage === 'continuing' ? '続きを生成中…'
    : stage?.stage === 'generating' ? '回答を作成中…' : '考え中…';
  return (
    <span className="mt-1 inline-flex items-center gap-2 rounded-full border border-violet-100 bg-violet-50/60 px-3 py-1.5" data-testid="assistant-thinking">
      {stage?.stage === 'tool'
        ? <MagnifyingGlass size={13} className="animate-pulse text-violet-500" />
        : <CircleNotch size={13} className="animate-spin text-violet-500" />}
      <span className="text-[12px] font-medium text-violet-700">{label}</span>
      <span className="flex gap-0.5">
        {[0, 1, 2].map((i) => (
          <span key={i} className="h-1 w-1 animate-bounce rounded-full bg-violet-400"
            style={{ animationDelay: `${i * 0.15}s` }} />
        ))}
      </span>
    </span>
  );
}

function ActionSuggestions({ items, running, onApprove, onDismiss }) {
  if (!items?.length) return null;
  const KIND_META = {
    create_task:        { emoji: '📌', color: 'sky',     label: 'タスク作成' },
    update_task_status: { emoji: '🔀', color: 'violet',  label: 'タスクステータス変更' },
    draft_report:       { emoji: '📝', color: 'emerald', label: '日報の下書き' },
    draft_announcement: { emoji: '📣', color: 'amber',   label: 'お知らせの下書き' },
  };
  const colorMap = {
    sky:     { bg: 'bg-sky-50',     border: 'border-sky-200',     text: 'text-sky-800',     btn: 'bg-teal-700 hover:bg-teal-800' },
    violet:  { bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-800',  btn: 'bg-teal-700 hover:bg-teal-800' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', btn: 'bg-teal-700 hover:bg-teal-800' },
    amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-800',   btn: 'bg-amber-600 hover:bg-amber-700' },
  };
  return (
    <div className="mt-3 space-y-2" data-testid="assistant-action-suggestions">
      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
        <Lightning size={11} weight="fill" className="text-amber-500" />
        提案されたアクション — ワンタップで実行
      </div>
      {items.map((s) => {
        const meta = KIND_META[s.kind] || { emoji: '⚙️', color: 'violet', label: s.kind };
        const c = colorMap[meta.color];
        const busy = running === s.id;
        return (
          <div key={s.id} className={`rounded-xl border ${c.border} ${c.bg} p-3 flex items-start gap-3`}
               data-testid={`assistant-action-suggestion-${s.kind}`}>
            <span className="text-lg leading-none pt-0.5">{meta.emoji}</span>
            <div className="min-w-0 flex-1">
              <p className={`text-xs font-bold ${c.text}`}>{s.label || meta.label}</p>
              {s.rationale && <p className="mt-0.5 text-[11px] text-zinc-600 line-clamp-2">{s.rationale}</p>}
              {s.kind === 'create_task' && s.params?.title && (
                <p className="mt-1 text-[10px] text-zinc-500 truncate">タスク名: {s.params.title}</p>
              )}
              {s.kind === 'update_task_status' && s.params?.status && (
                <p className="mt-1 text-[10px] text-zinc-500">→ {s.params.status}</p>
              )}
              {s.kind === 'draft_announcement' && s.params?.title && (
                <p className="mt-1 text-[10px] text-zinc-500 truncate">件名: {s.params.title}</p>
              )}
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <button onClick={() => onApprove(s)} disabled={busy}
                data-testid={`assistant-action-approve-${s.id}`}
                className={`inline-flex items-center gap-1 rounded-lg ${c.btn} text-white px-2.5 py-1 text-[11px] font-semibold disabled:opacity-50 transition-colors`}>
                {busy ? <><CircleNotch size={11} className="animate-spin" /> 実行中…</> : <>実行</>}
              </button>
              <button onClick={() => onDismiss(s.id)} disabled={busy}
                data-testid={`assistant-action-dismiss-${s.id}`}
                className="rounded-lg border border-zinc-200 bg-white px-2.5 py-1 text-[10px] text-zinc-500 hover:bg-zinc-100 disabled:opacity-50">
                却下
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Followups({ items, onPick }) {
  if (!items?.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="assistant-followups">
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400 pr-1">
        <Lightbulb size={11} weight="fill" className="text-amber-400" /> 次に聞くなら
      </span>
      {items.slice(0, 3).map((q, i) => (
        <button key={`${q}-${i}`} type="button" onClick={() => onPick(q)}
          data-testid={`assistant-followup-${i}`}
          className="inline-flex items-center gap-1 max-w-[280px] px-2.5 py-1 rounded-full text-[11px]
                     bg-amber-50 border border-amber-200 text-amber-800
                     hover:bg-amber-100 hover:border-amber-400 hover:shadow-sm
                     active:scale-95 transition-all cursor-pointer">
          <span className="truncate">{q}</span>
        </button>
      ))}
    </div>
  );
}

function FeedbackButtons({ msg, onFeedback }) {
  if (!msg.id || String(msg.id).startsWith('local-') || String(msg.id).startsWith('a-')) return null;
  const cur = msg.feedback;
  return (
    <span className="ml-1 inline-flex items-center gap-0.5">
      <button type="button" title="良い回答"
        onClick={() => onFeedback(msg, cur === 'up' ? '' : 'up')}
        data-testid={`assistant-feedback-up-${msg.id}`}
        className={`rounded p-1 transition-colors ${cur === 'up'
          ? 'text-emerald-600 bg-emerald-50'
          : 'text-zinc-300 opacity-0 group-hover:opacity-100 hover:text-emerald-600 hover:bg-emerald-50'}`}>
        <ThumbsUp size={13} weight={cur === 'up' ? 'fill' : 'regular'} />
      </button>
      <button type="button" title="不正確・役に立たない"
        onClick={() => onFeedback(msg, cur === 'down' ? '' : 'down')}
        data-testid={`assistant-feedback-down-${msg.id}`}
        className={`rounded p-1 transition-colors ${cur === 'down'
          ? 'text-rose-600 bg-rose-50'
          : 'text-zinc-300 opacity-0 group-hover:opacity-100 hover:text-rose-600 hover:bg-rose-50'}`}>
        <ThumbsDown size={13} weight={cur === 'down' ? 'fill' : 'regular'} />
      </button>
    </span>
  );
}

export default function AssistantPanel({ onBack, attachedMessage, onClearAttached }) {
  const [docMode, setDocMode] = useState(false);
  const [convs, setConvs] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [streamMeta, setStreamMeta] = useState(null);
  const [streamStage, setStreamStage] = useState(null);
  const [streamTrail, setStreamTrail] = useState([]);
  const [lastFailed, setLastFailed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followups, setFollowups] = useState([]);      // last-answer follow-up chips
  const [followupsFor, setFollowupsFor] = useState(null); // message id chips belong to
  const [actionSuggestions, setActionSuggestions] = useState([]); // AI-suggested action cards
  const [actionSuggestionsFor, setActionSuggestionsFor] = useState(null);
  const [actionRunning, setActionRunning] = useState(null); // suggestion id being executed
  const [aiSel, setAiSel] = useState(null); // 会話ごとの使用モデル/プリセット選択
  const abortRef = useRef(null);
  const sendLockRef = useRef(false); // 二重送信防止（state更新前の連打・Enter連打対策）
  const jobRef = useRef(null); // { id, convId, cursor } — 停止・再接続（レジューム）用
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, streamText]);

  const sendFeedback = async (msg, rating) => {
    try {
      await axios.post(`${API}/assistant/messages/${msg.id}/feedback`, { rating });
      setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, feedback: rating || undefined } : m));
      if (rating === 'down') toast.success('フィードバックを送信しました — 改善に活用します');
      else if (rating === 'up') toast.success('フィードバックを送信しました');
    } catch { toast.error('フィードバックの送信に失敗しました'); }
  };

  const saveAsNote = async (msg) => {
    try {
      const res = await axios.post(`${API}/assistant/messages/${msg.id}/save-as-note`, {});
      const url = `${window.location.origin}/knowledge/p/${res.data.article_id}`;
      toast.success('ナレッジ下書きとして保存しました', {
        action: {
          label: '開く',
          onClick: () => window.open(url, '_blank'),
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ノートの保存に失敗しました');
    }
  };

  const fetchFollowups = useCallback(async (question, answer, msgId) => {
    try {
      const res = await axios.post(`${API}/crawler/followups`, {
        query: question, context: answer || '', limit: 3,
      });
      const items = Array.isArray(res.data?.suggestions) ? res.data.suggestions : [];
      setFollowups(items);
      setFollowupsFor(msgId);
    } catch (_e) { setFollowups([]); setFollowupsFor(null); }
  }, []);

  const fetchActionSuggestions = useCallback(async (question, answer, msgId, convId) => {
    try {
      const res = await axios.post(`${API}/assistant/actions/suggest`, {
        query: question, answer: answer || '',
        conversation_id: convId, message_id: msgId, max_suggestions: 2,
      });
      const items = Array.isArray(res.data?.suggestions) ? res.data.suggestions : [];
      setActionSuggestions(items);
      setActionSuggestionsFor(msgId);
    } catch (_e) {
      setActionSuggestions([]);
      setActionSuggestionsFor(null);
    }
  }, []);

  const dismissSuggestion = (sid) => {
    setActionSuggestions((prev) => prev.filter((s) => s.id !== sid));
  };

  const approveSuggestion = async (s) => {
    if (actionRunning) return;
    setActionRunning(s.id);
    try {
      const res = await axios.post(`${API}/assistant/actions/execute`, {
        kind: s.kind, params: s.params || {},
        conversation_id: activeConv?.id, message_id: actionSuggestionsFor,
      });
      const aid = res.data.action_id;
      const secs = Number(res.data.undo_window_seconds || 300);
      toast.success(`${s.label || res.data.label} を実行しました`, {
        duration: secs * 1000,
        action: { label: '取り消し', onClick: () => undoAction(aid) },
      });
      // Remove the executed suggestion — keep the rest so user can approve more
      dismissSuggestion(s.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'アクション実行に失敗しました');
    } finally {
      setActionRunning(null);
    }
  };

  const loadConversations = useCallback(async () => {
    const res = await axios.get(`${API}/assistant/conversations`);
    setConvs(res.data || []);
    window.dispatchEvent(new CustomEvent('assistant-convs-changed-silent'));
    return res.data || [];
  }, []);

  const openConversation = useCallback(async (conv) => {
    setActiveConv(conv);
    setLastFailed(null);
    setFollowups([]); setFollowupsFor(null);
    setActionSuggestions([]); setActionSuggestionsFor(null);
    window.dispatchEvent(new CustomEvent('assistant-active-conv', { detail: { id: conv.id } }));
    try {
      const res = await axios.get(`${API}/assistant/conversations/${conv.id}/messages`);
      setMessages(res.data || []);
    } catch (_e) { setMessages([]); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const list = await loadConversations();
        if (list.length) await openConversation(list[0]);
      } catch (_e) { /* ignore */ }
      setLoading(false);
    })();
  }, [loadConversations, openConversation]);

  const newConversation = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/assistant/conversations`);
      setConvs((prev) => [res.data, ...prev]);
      setActiveConv(res.data);
      setMessages([]);
      setLastFailed(null);
      setFollowups([]); setFollowupsFor(null);
      setActionSuggestions([]); setActionSuggestionsFor(null);
      window.dispatchEvent(new CustomEvent('assistant-convs-changed'));
      window.dispatchEvent(new CustomEvent('assistant-active-conv', { detail: { id: res.data.id } }));
    } catch (_e) { toast.error('会話の作成に失敗しました'); }
  }, []);

  // Bridge: sidebar conversation list (AssistantConvList) → this panel
  useEffect(() => {
    const onOpen = (e) => {
      if (e.detail?.new) { newConversation(); return; }
      if (e.detail?.id) openConversation({ id: e.detail.id });
    };
    const onDeleted = async (e) => {
      if (activeConv?.id === e.detail?.id) {
        const list = await loadConversations().catch(() => []);
        if (list.length) await openConversation(list[0]);
        else { setActiveConv(null); setMessages([]); }
      } else {
        loadConversations().catch(() => {});
      }
    };
    window.addEventListener('assistant-open-conv', onOpen);
    window.addEventListener('assistant-conv-deleted', onDeleted);
    return () => {
      window.removeEventListener('assistant-open-conv', onOpen);
      window.removeEventListener('assistant-conv-deleted', onDeleted);
    };
  }, [newConversation, openConversation, loadConversations, activeConv]);

  const [shareOpen, setShareOpen] = useState(false);

  const shareConversation = async (expiresDays) => {
    if (!activeConv) return;
    setShareOpen(false);
    try {
      const res = await axios.post(`${API}/assistant/conversations/${activeConv.id}/share`,
        { expires_days: expiresDays });
      const url = `${window.location.origin}${res.data.path}`;
      try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
      const expNote = res.data.expires_at
        ? `（${jstDateTime(res.data.expires_at)} まで有効）` : '（無期限）';
      toast.success(`共有リンクをコピーしました ${expNote} — 社内メンバーのみ・読み取り専用`);
      window.dispatchEvent(new CustomEvent('assistant-convs-changed'));
    } catch { toast.error('共有リンクの作成に失敗しました'); }
  };

  const revokeShare = async () => {
    if (!activeConv) return;
    setShareOpen(false);
    try {
      await axios.delete(`${API}/assistant/conversations/${activeConv.id}/share`);
      toast.success('共有を解除しました — リンクは無効になりました');
      window.dispatchEvent(new CustomEvent('assistant-convs-changed'));
    } catch { toast.error('共有の解除に失敗しました'); }
  };

  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const openShareAnalytics = async () => {
    if (!activeConv) return;
    setShareOpen(false);
    try {
      const res = await axios.get(`${API}/assistant/conversations/${activeConv.id}/share-analytics`);
      setAnalytics(res.data);
      setAnalyticsOpen(true);
    } catch {
      toast.error('閲覧統計を取得できませんでした');
    }
  };

  // ── AI Action Framework (Phase 2) ──────────────────────────────────────────
  const [actionsOpen, setActionsOpen] = useState(false);
  const [actionsList, setActionsList] = useState([]);

  const loadRecentActions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/assistant/actions/recent?limit=20`);
      setActionsList(res.data?.items || []);
    } catch { setActionsList([]); }
  }, []);

  const openActionsHistory = async () => {
    await loadRecentActions();
    setActionsOpen(true);
  };

  // ── Delete active conversation ────────────────────────────────────────────
  const deleteCurrentConversation = async () => {
    if (!activeConv) return;
    if (!window.confirm(`「${activeConv.title || '無題の会話'}」を削除しますか？この操作は取り消せません。`)) return;
    try {
      await axios.delete(`${API}/assistant/conversations/${activeConv.id}`);
      toast.success('会話を削除しました');
      setActiveConv(null);
      setMessages([]);
      setStreamTrail([]);
      setFollowups([]); setFollowupsFor(null);
      setActionSuggestions([]); setActionSuggestionsFor(null);
      window.dispatchEvent(new CustomEvent('assistant-conv-deleted', { detail: { id: activeConv.id } }));
      window.dispatchEvent(new CustomEvent('assistant-convs-changed'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || '削除に失敗しました');
    }
  };

  const undoAction = async (aid) => {
    try {
      await axios.post(`${API}/assistant/actions/${aid}/undo`);
      toast.success('アクションを取り消しました');
      loadRecentActions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '取り消しに失敗しました');
    }
  };

  // Expose an executor that shows an undo toast — used by inline suggestions.
  const executeAction = async ({ kind, params, label }) => {
    try {
      const res = await axios.post(`${API}/assistant/actions/execute`, {
        kind, params, conversation_id: activeConv?.id,
      });
      const aid = res.data.action_id;
      const secs = Number(res.data.undo_window_seconds || 300);
      toast.success(`${label || kind} を実行しました`, {
        duration: secs * 1000,
        action: {
          label: '取り消し',
          onClick: () => undoAction(aid),
        },
      });
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'アクション実行に失敗しました');
      return null;
    }
  };

  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFile, setUploadingFile] = useState(null); // { name, size }
  const [previewAtt, setPreviewAtt] = useState(null);
  const fileRef = useRef(null);
  // Drag-drop attach: track whether a drag is currently over the panel and
  // count enter/leave events so nested children don't flicker the overlay.
  const [dragActive, setDragActive] = useState(false);
  const dragDepthRef = useRef(0);

  const uploadFile = async (f) => {
    if (!f || uploading) return;
    setUploading(true); setUploadProgress(0);
    setUploadingFile({ name: f.name, size: f.size });
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await axios.post(`${API}/assistant/attachments`, fd, {
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      setAttachments((prev) => [...prev, res.data].slice(0, 5));
      toast.success(`「${res.data.filename}」を読み取りました（${res.data.chars.toLocaleString()}文字）`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ファイルの読み取りに失敗しました');
    } finally {
      setUploading(false); setUploadProgress(0); setUploadingFile(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Drag-drop handlers on the whole panel. We rely on a depth counter so
  // that dragging over nested children (message bubbles, input, etc.)
  // doesn't cause the overlay to flicker. Only files (`Files` in
  // dataTransfer.types) trigger the overlay — dragging text/URLs from
  // inside the page is ignored so we don't hijack normal selections.
  const isFileDrag = (e) => {
    const types = e?.dataTransfer?.types;
    if (!types) return false;
    for (let i = 0; i < types.length; i += 1) {
      if (types[i] === 'Files') return true;
    }
    return false;
  };
  const onPanelDragEnter = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    if (!dragActive) setDragActive(true);
  };
  const onPanelDragOver = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const onPanelDragLeave = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragActive(false);
  };
  const onPanelDrop = async (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setDragActive(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    if (uploading) {
      toast.error('別のファイルをアップロード中です。完了後に再度お試しください。');
      return;
    }
    // Cap at 5 total attachments (matches the paperclip flow)
    let remaining = Math.max(0, 5 - attachments.length);
    if (remaining <= 0) {
      toast.error('添付は最大5件までです。既存の添付を削除してから再度お試しください。');
      return;
    }
    for (const f of files) {
      if (remaining <= 0) {
        toast.error('添付上限（5件）に達したため、残りのファイルはスキップしました。');
        break;
      }
      // Sequential to reuse the single uploading spinner state
      await uploadFile(f);
      remaining -= 1;
    }
  };

  const deleteMessage = async (mid) => {
    if (!mid || String(mid).startsWith('local-') || String(mid).startsWith('a-')) {
      // Local-only message: just remove client-side
      setMessages((prev) => prev.filter((m) => m.id !== mid));
      return;
    }
    if (!window.confirm('このメッセージを削除しますか？')) return;
    try {
      await axios.delete(`${API}/assistant/messages/${mid}`);
      setMessages((prev) => prev.filter((m) => m.id !== mid));
      toast.success('メッセージを削除しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '削除に失敗しました');
    }
  };

  // 「続きを生成」— 途切れた/停止した回答を同じバブル内で正確な位置から再開する。
  // サーバーは新しく生成された差分テキストのみをSSEで送り、同じメッセージDocを更新する。
  const [contMsgId, setContMsgId] = useState(null);
  const continueMessage = async (msg) => {
    if (streaming || contMsgId || !activeConv || !msg?.id) return;
    setContMsgId(msg.id);
    let acc = msg.text || '';
    try {
      const resp = await fetch(`${API}/assistant/conversations/${activeConv.id}/messages/${msg.id}/continue`, {
        method: 'POST', credentials: 'include',
      });
      if (!resp.ok) {
        const errBody = await resp.text().catch(() => '');
        let m = '続きの生成に失敗しました';
        try { m = JSON.parse(errBody).detail || m; } catch { /* ignore */ }
        throw new Error(m);
      }
      const reader = resp.body.getReader();
      const dec = new TextDecoder('utf-8');
      let buf = '';
      let done = false;
      let stillIncomplete = false;
      while (!done) {
        const r = await reader.read();
        if (r.done) break;
        buf += dec.decode(r.value, { stream: true });
        let idx;
        while ((idx = buf.indexOf('\n\n')) >= 0) {
          const frame = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          if (frame.startsWith(':')) continue;
          let evt = 'message';
          let data = '';
          for (const raw of frame.split('\n')) {
            if (raw.startsWith('event:')) evt = raw.slice(6).trim();
            else if (raw.startsWith('data:')) { data = raw.slice(5); if (data.startsWith(' ')) data = data.slice(1); }
          }
          if (evt === 'error') {
            let d = null;
            try { d = JSON.parse(data)?.error; } catch { /* ignore */ }
            throw new Error(d || '続きの生成に失敗しました');
          } else if (evt === 'status') {
            try { if (JSON.parse(data)?.stage === 'incomplete') stillIncomplete = true; } catch { /* ignore */ }
          } else if (data === '[DONE]') {
            done = true;
            break;
          } else if (evt === 'message' && data) {
            try {
              const o = JSON.parse(data);
              if (o && typeof o.t === 'string') acc += o.t;
            } catch { acc += data.replace(/\\n/g, '\n'); }
            const t = acc;
            setMessages((prev) => prev.map((m2) => (m2.id === msg.id ? { ...m2, text: t } : m2)));
          }
        }
      }
      if (stillIncomplete) {
        toast.warning('まだ未完了の可能性があります。もう一度「続きを生成」を押してください。', { duration: 5000 });
      } else {
        toast.success('続きの生成が完了しました');
      }
    } catch (e) {
      toast.error(jaError(e.message) || '続きの生成に失敗しました。部分的な回答は保持されています。');
    } finally {
      setContMsgId(null);
      try {
        const res = await axios.get(`${API}/assistant/conversations/${activeConv.id}/messages`);
        setMessages(res.data || []);
      } catch { /* keep local state */ }
    }
  };

  const send = async (text, selectedIds = [], retry = false) => {
    const t = (text || '').trim();
    if (!t || streaming || sendLockRef.current) return;
    if (aiSel && aiSel.selectable && !aiSel.ready) {
      toast.error('現在利用可能なテキスト生成モデルがありません。AI管理者にモデルの読み込み状況を確認してください。');
      return;
    }
    sendLockRef.current = true;
    let conv = activeConv;
    if (!conv) {
      try {
        const res = await axios.post(`${API}/assistant/conversations`);
        conv = res.data;
        setConvs((prev) => [conv, ...prev]);
        setActiveConv(conv);
      } catch (_e) { sendLockRef.current = false; toast.error('会話の作成に失敗しました'); return; }
    }
    setLastFailed(null);
    setInput('');
    setFollowups([]); setFollowupsFor(null);
    setActionSuggestions([]); setActionSuggestionsFor(null);
    setStreamTrail([]);
    if (!retry) setMessages((prev) => [...prev, { id: `local-${Date.now()}`, role: 'user', text: t, created_at: new Date().toISOString() }]);
    setStreaming(true);
    setStreamText('');
    setStreamMeta(null);
    setStreamStage({ stage: 'routing' });
    setStreamTrail([{ stage: 'routing', at: Date.now() }]);
    if (attachedMessage && selectedIds.includes(attachedMessage.id)) onClearAttached?.();

    // クライアント側 冪等性キー — ネットワーク再送・二重送信でも
    // サーバーは同じ生成ジョブに接続するだけで、メッセージは重複しない
    const idem = (window.crypto?.randomUUID?.()
      || `${Date.now()}-${Math.random().toString(16).slice(2)}`);
    jobRef.current = null;
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let acc = '';
    let meta = null;
    let sawDone = false;

    // 1本のSSEレスポンスを読み切る（POST初回・GET再接続の両方で共用）
    const consume = async (resp) => {
      const reader = resp.body.getReader();
      const dec = new TextDecoder('utf-8');
      let buf = '';
      while (true) {
        const r = await reader.read();
        if (r.done) return;
        buf += dec.decode(r.value, { stream: true });
        let idx;
        while ((idx = buf.indexOf('\n\n')) >= 0) {
          const frame = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          // SSEコメント行（`: heartbeat`）— プロキシを生かす不可視ハートビート
          if (frame.startsWith(':')) continue;
          let evt = 'message';
          let data = '';
          let cursorId = null;
          for (const raw of frame.split('\n')) {
            if (raw.startsWith('event:')) evt = raw.slice(6).trim();
            else if (raw.startsWith('data:')) { data = raw.slice(5); if (data.startsWith(' ')) data = data.slice(1); }
            else if (raw.startsWith('id:')) cursorId = parseInt(raw.slice(3).trim(), 10);
          }
          if (evt === 'job') {
            try {
              const j = JSON.parse(data);
              jobRef.current = { id: j.job_id, convId: conv.id, cursor: jobRef.current?.cursor || 0 };
            } catch { /* ignore */ }
          } else if (evt === 'meta') {
            try { meta = JSON.parse(data); setStreamMeta(meta); } catch { /* ignore */ }
          } else if (evt === 'status') {
            try {
              const st = JSON.parse(data);
              if (st.stage === 'continue_failed') {
                toast.warning('回答が完了する前に中断されました。「続きを生成」ボタンで再開できます。', { duration: 6000 });
              }
              setStreamStage(st);
              setStreamTrail((prev) => {
                if (prev.length && prev[prev.length - 1].stage === st.stage
                    && prev[prev.length - 1].label === st.label) return prev;
                return [...prev, { ...st, at: Date.now() }];
              });
            } catch { /* ignore */ }
          } else if (evt === 'error') {
            let errDetail = null;
            try { errDetail = JSON.parse(data)?.error || null; } catch { /* ignore */ }
            const err = new Error(errDetail || '回答の生成に失敗しました');
            err.serverError = true;
            throw err;
          } else if (data === '[DONE]') {
            sawDone = true;
            return;
          } else if (evt === 'message' && data) {
            try {
              const o = JSON.parse(data);
              if (o && typeof o.t === 'string') acc += o.t;
            } catch { acc += data.replace(/\\n/g, '\n'); }
            if (Number.isFinite(cursorId) && jobRef.current) jobRef.current.cursor = cursorId;
            setStreamText(acc);
          }
        }
      }
    };

    try {
      const resp = await fetch(`${API}/assistant/conversations/${conv.id}/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: t, selected_message_ids: selectedIds, retry,
          attachment_ids: attachments.map((a) => a.id), idempotency_key: idem,
          model_id: aiSel?.modelId || null, preset_id: aiSel?.presetId || null,
        }),
        signal: ctrl.signal,
      });
      if (!resp.ok) {
        const errBody = await resp.text().catch(() => '');
        let msg = '回答の生成に失敗しました';
        try { msg = JSON.parse(errBody).detail || msg; } catch { /* ignore */ }
        const err = new Error(msg);
        err.serverError = true;
        throw err;
      }
      try {
        await consume(resp);
      } catch (readErr) {
        // 読み取り中の切断（プロキシ/Cloudflare/ネットワーク）—
        // 生成ジョブはサーバー側で継続中なのでカーソル位置から再接続する
        if (readErr.name === 'AbortError' || readErr.serverError || !jobRef.current?.id) throw readErr;
        toast.info('接続が中断されました。再接続しています…', { duration: 3000 });
        let resumed = false;
        for (let i = 0; i < 3 && !resumed; i += 1) {
          await new Promise((res) => { setTimeout(res, 1200 * (i + 1)); });
          try {
            const r2 = await fetch(
              `${API}/assistant/conversations/${conv.id}/jobs/${jobRef.current.id}/stream?cursor=${jobRef.current.cursor || 0}`,
              { credentials: 'include', signal: ctrl.signal },
            );
            if (!r2.ok) continue;
            await consume(r2);
            resumed = true;
          } catch (e2) {
            if (e2.name === 'AbortError' || e2.serverError) throw e2;
          }
        }
        if (!resumed) {
          const err = new Error('接続が中断されました。回答はサーバー側で生成が続いています — しばらくしてから会話を開き直すと結果が表示されます。');
          err.connectionLost = true;
          throw err;
        }
      }
      if (!sawDone && acc.length > 0) {
        toast.warning('接続が中断されました（回答が未完了の可能性があります）。「続きを生成」で再開できます。', { duration: 6000 });
      }
      let latestAssistantId = null;
      const finalTrail = streamTrail;
      const finalHints = meta?.export_hints || [];
      try {
        const res = await axios.get(`${API}/assistant/conversations/${conv.id}/messages`);
        const list = res.data || [];
        // Find the newest assistant message id, then attach the local trail
        const asstMsgs = list.filter((m) => m.role === 'assistant');
        latestAssistantId = asstMsgs.length ? asstMsgs[asstMsgs.length - 1].id : null;
        setMessages(list.map((m) => (m.id === latestAssistantId
          ? { ...m, trail: finalTrail, export_hints: finalHints }
          : m)));
      } catch {
        const fallbackId = `a-${Date.now()}`;
        setMessages((prev) => [...prev, {
          id: fallbackId, role: 'assistant', text: acc,
          sources: meta?.sources || [], used_selection: meta?.used_selection,
          created_at: new Date().toISOString(), trail: finalTrail, export_hints: finalHints,
        }]);
        latestAssistantId = fallbackId;
      }
      loadConversations().catch(() => {});
      setAttachments([]);
      window.dispatchEvent(new CustomEvent('assistant-convs-changed'));
      setTimeout(() => window.dispatchEvent(new CustomEvent('assistant-convs-changed')), 6000);
      // Fetch 3 tappable follow-up chips based on the just-answered question
      if (latestAssistantId) {
        fetchFollowups(t, acc, latestAssistantId);
        fetchActionSuggestions(t, acc, latestAssistantId, conv.id);
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        if (acc) {
          setMessages((prev) => [...prev, {
            id: `a-${Date.now()}`, role: 'assistant', text: acc, status: 'stopped',
            sources: meta?.sources || [], used_selection: meta?.used_selection,
            created_at: new Date().toISOString(),
          }]);
        }
      } else if (e.connectionLost) {
        toast.error(e.message, { duration: 8000 });
        // 生成はサーバー側で継続 — 保存され次第、取り込み直す
        setTimeout(async () => {
          try {
            const res = await axios.get(`${API}/assistant/conversations/${conv.id}/messages`);
            setMessages(res.data || []);
          } catch { /* ignore */ }
        }, 5000);
      } else {
        setLastFailed({ text: t, selectedIds, message: jaError(e.message) });
      }
    } finally {
      sendLockRef.current = false;
      abortRef.current = null;
      setStreaming(false);
      setStreamText('');
      setStreamMeta(null);
    }
  };

  const stop = () => {
    const job = jobRef.current;
    abortRef.current?.abort();
    // バックエンドにも停止を通知 — サーバー側の生成を止め、
    // 部分回答は「停止済み」メッセージとして保存される
    if (job?.id && job?.convId) {
      axios.post(`${API}/assistant/conversations/${job.convId}/jobs/${job.id}/cancel`).catch(() => {});
      // 保存済みの部分回答と表示を同期する（保存が完了するまで最大3回ポーリング）
      let tries = 0;
      const sync = async () => {
        tries += 1;
        if (sendLockRef.current) return; // 新しい送信が始まっていたら中断
        try {
          const res = await axios.get(`${API}/assistant/conversations/${job.convId}/messages`);
          const list = res.data || [];
          if (list.length && list[list.length - 1].role === 'assistant') {
            setMessages(list);
            return;
          }
        } catch { /* ignore */ }
        if (tries < 3) setTimeout(sync, 1500);
      };
      setTimeout(sync, 1500);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input, attachedMessage ? [attachedMessage.id] : []);
    }
  };

  const empty = !loading && messages.length === 0 && !streaming;

  return (
    <div
      className="flex-1 flex flex-col min-h-0 relative"
      data-testid="assistant-panel"
      onDragEnter={onPanelDragEnter}
      onDragOver={onPanelDragOver}
      onDragLeave={onPanelDragLeave}
      onDrop={onPanelDrop}
    >
      {/* Drag-drop overlay — appears while a file is being dragged over the
          chat panel. Non-blocking (pointer-events-none) so children still
          receive drag events; only shown when a file (not text) is dragged. */}
      {dragActive && (
        <div
          className="absolute inset-0 z-40 pointer-events-none flex items-center justify-center bg-violet-500/10 backdrop-blur-[2px]"
          data-testid="assistant-drop-overlay"
        >
          <div className="rounded-lg border-2 border-dashed border-violet-500 bg-white/95 px-6 py-5 text-center shadow-lg">
            <div className="text-violet-700 font-bold text-sm">ここにドロップして添付</div>
            <div className="text-[11px] text-zinc-500 mt-1">画像 / PDF / Office / ZIP（最大5件）</div>
          </div>
        </div>
      )}
      {/* 文書生成モード — チャットの上に重ねるだけでチャット本体は一切変更しない */}
      {docMode && (
        <div className="absolute inset-x-0 bottom-0 top-[57px] z-30 bg-white overflow-hidden" data-testid="docgen-overlay">
          <DocGenPanel />
        </div>
      )}
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
        <button type="button" onClick={onBack} data-testid="assistant-back-btn"
          className="md:hidden p-1.5 -ml-1.5 rounded text-zinc-500 hover:bg-zinc-100 shrink-0" title="一覧へ戻る">
          <CaretLeft size={18} weight="bold" />
        </button>
        <span className="w-7 h-7 rounded-lg bg-teal-700 text-white flex items-center justify-center shrink-0">
          <Sparkle size={15} weight="fill" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-zinc-900 truncate" data-testid="assistant-title">Sensbey AIアシスタント</p>
          <p className="text-[10px] text-zinc-400 truncate">プライベートな会話です（他の社員には表示されません）</p>
        </div>
        {/* モード切替: チャット / 文書生成 */}
        <div className="hidden sm:inline-flex rounded-full border border-zinc-200 overflow-hidden ml-2 shrink-0" data-testid="assistant-mode-toggle">
          <button type="button" onClick={() => setDocMode(false)} data-testid="assistant-mode-chat"
            className={`px-3 py-1 text-[11px] font-bold transition-colors ${!docMode ? 'bg-violet-600 text-white' : 'bg-white text-zinc-500 hover:bg-zinc-50'}`}>
            チャット
          </button>
          <button type="button" onClick={() => setDocMode(true)} data-testid="assistant-mode-doc"
            className={`px-3 py-1 text-[11px] font-bold transition-colors ${docMode ? 'bg-violet-600 text-white' : 'bg-white text-zinc-500 hover:bg-zinc-50'}`}>
            文書生成
          </button>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <div className="relative">
            <button type="button" onClick={() => setShareOpen((s) => !s)} disabled={!activeConv}
              data-testid="assistant-share-btn" title="この会話を読み取り専用リンクで共有"
              className="inline-flex items-center gap-1 px-2.5 h-8 rounded-lg text-xs font-medium text-zinc-500 hover:text-violet-700 hover:bg-violet-50 disabled:opacity-40 transition-colors">
              <ShareNetwork size={13} /> 共有
            </button>
            {shareOpen && (
              <div className="absolute right-0 top-full mt-1 w-52 rounded-xl border border-zinc-200 bg-white p-1.5 shadow-lg z-30" data-testid="assistant-share-menu">
                <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">リンクの有効期限</p>
                {[[1, '24時間'], [7, '7日間'], [30, '30日間'], [null, '無期限']].map(([d, label]) => (
                  <button key={label} type="button" onClick={() => shareConversation(d)}
                    data-testid={`assistant-share-exp-${d ?? 'none'}`}
                    className="w-full rounded-lg px-2 py-1.5 text-left text-xs text-zinc-700 hover:bg-violet-50 hover:text-violet-700 transition-colors">
                    {label}でリンクをコピー
                  </button>
                ))}
                <div className="my-1 border-t border-zinc-100" />
                <button type="button" onClick={openShareAnalytics}
                  data-testid="assistant-share-analytics"
                  className="w-full rounded-lg px-2 py-1.5 text-left text-xs text-violet-700 hover:bg-violet-50 transition-colors">
                  閲覧統計を見る
                </button>
                <button type="button" onClick={revokeShare} data-testid="assistant-share-revoke"
                  className="w-full rounded-lg px-2 py-1.5 text-left text-xs text-rose-600 hover:bg-rose-50 transition-colors">
                  共有を解除
                </button>
              </div>
            )}
          </div>
          <button type="button" onClick={openActionsHistory}
            data-testid="assistant-actions-btn" title="AIが実行したアクションの履歴と取り消し"
            className="inline-flex items-center gap-1 px-2.5 h-8 rounded-lg text-xs font-medium text-zinc-500 hover:text-amber-600 hover:bg-amber-50 transition-colors">
            <Lightning size={13} weight="fill" /> アクション
          </button>
          {activeConv && (
            <button type="button" onClick={deleteCurrentConversation}
              data-testid="assistant-delete-conv-btn" title="この会話を削除"
              className="inline-flex items-center justify-center px-2 h-8 rounded-lg text-zinc-400 hover:text-rose-600 hover:bg-rose-50 transition-colors">
              <Trash size={13} />
            </button>
          )}
          <button type="button" onClick={newConversation} data-testid="assistant-new-conv-btn"
            className="inline-flex items-center gap-1 px-2.5 h-8 rounded-lg text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 transition-colors">
            <Plus size={13} weight="bold" /> 新しい会話
          </button>
        </div>
      </div>
      {/* 使用モデル / 推論プリセット — 会話ごとに保存（管理ルーティングは変更しない） */}
      <ModelPresetPicker compact feature="sensbey_chat_assistant"
        instanceId={activeConv?.id || null} onChange={setAiSel} disabled={streaming}
        wrapperClassName="px-4 py-1.5 border-b border-zinc-100 bg-zinc-50/60" />

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-2" data-testid="assistant-message-list">
        {loading && <p className="p-8 text-center text-xs text-zinc-400">読み込み中…</p>}
        {empty && (
          <div className="max-w-lg mx-auto px-6 py-8 text-center" data-testid="assistant-empty-state">
            <span className="inline-flex w-12 h-12 rounded-lg bg-teal-700 text-white items-center justify-center mb-3">
              <Sparkle size={22} weight="fill" />
            </span>
            <p className="text-sm font-semibold text-zinc-800 mb-1">何でも聞いてください</p>
            <p className="text-xs text-zinc-500 mb-5">ナレッジ・営業・製造・IT・勤怠・資材・プロジェクトの情報も、あなたの権限の範囲で参照できます。</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-left">
              {STARTERS.map((s) => (
                <button key={s} type="button" onClick={() => send(s)}
                  data-testid={`assistant-starter-${STARTERS.indexOf(s)}`}
                  className="px-3 py-2.5 rounded-xl border border-zinc-200 bg-white text-xs text-zinc-700 hover:border-violet-300 hover:bg-violet-50/60 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          m.role === 'user' ? (
            <div key={m.id} className="group flex justify-end px-4 py-1.5 gap-1 items-start" data-testid={`assistant-user-msg-${m.id}`}>
              <button type="button" onClick={() => deleteMessage(m.id)}
                data-testid={`assistant-user-msg-delete-${m.id}`}
                className="opacity-0 group-hover:opacity-100 mt-1 p-1 rounded text-zinc-400 hover:text-rose-600 hover:bg-rose-50 transition-all"
                title="このメッセージを削除">
                <Trash size={11} />
              </button>
              <div className="max-w-[85%] rounded-lg rounded-br-md bg-teal-700 text-white px-3.5 py-2 text-sm whitespace-pre-wrap break-words">{m.text}</div>
            </div>
          ) : (
            <AssistantMessage key={m.id} msg={{ ...m, continuing: m.id === contMsgId }}
              onFeedback={sendFeedback} onSaveNote={saveAsNote} onDelete={deleteMessage}
              onContinue={continueMessage} />
          )
        ))}
        {streaming && (
          <AssistantMessage msg={{ text: streamText, streaming: true, sources: streamMeta?.sources, stage: streamStage, trail: streamTrail, export_hints: streamMeta?.export_hints }} />
        )}
        {!streaming && actionSuggestions.length > 0 && actionSuggestionsFor
          && messages.length > 0
          && messages[messages.length - 1]?.id === actionSuggestionsFor && (
          <div className="px-4 pb-2 pl-[3.75rem]">
            <ActionSuggestions items={actionSuggestions} running={actionRunning}
              onApprove={approveSuggestion} onDismiss={dismissSuggestion} />
          </div>
        )}
        {!streaming && followups.length > 0 && followupsFor && messages.length > 0
          && messages[messages.length - 1]?.id === followupsFor && (
          <div className="px-4 pb-2 pl-[3.75rem]">
            <Followups items={followups} onPick={(q) => { setFollowups([]); setFollowupsFor(null); send(q); }} />
          </div>
        )}
        {lastFailed && (
          <div className="mx-4 my-2 px-3 py-2.5 rounded-xl bg-red-50 border border-red-100 flex items-center gap-2" data-testid="assistant-error">
            <p className="flex-1 text-xs text-red-700">{lastFailed.message}</p>
            <button type="button" onClick={() => send(lastFailed.text, lastFailed.selectedIds, true)}
              data-testid="assistant-retry-btn"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white border border-red-200 text-xs font-medium text-red-700 hover:bg-red-100 transition-colors">
              <ArrowsClockwise size={12} /> 再試行
            </button>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Attached chat message */}
      {attachedMessage && (
        <div className="mx-3 mb-1.5 rounded-xl border border-violet-200 bg-violet-50/60 px-3 py-2" data-testid="assistant-attached-message">
          <div className="flex items-center gap-2">
            <p className="flex-1 min-w-0 text-[11px] text-zinc-600 truncate">
              <span className="font-semibold text-violet-700">選択中のメッセージ</span>
              <span className="mx-1 text-zinc-400">·</span>
              <b><span data-no-translate>{attachedMessage.user_name}</span></b>: {attachedMessage.text}
            </p>
            <button type="button" onClick={onClearAttached} data-testid="assistant-attached-clear"
              className="p-1 rounded text-zinc-400 hover:text-zinc-700 hover:bg-white"><X size={13} /></button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {QUICK_ACTIONS.map((a) => (
              <button key={a} type="button" onClick={() => send(a, [attachedMessage.id])}
                data-testid={`assistant-quick-${QUICK_ACTIONS.indexOf(a)}`}
                className="px-2 py-1 rounded-lg bg-white border border-violet-200 text-[11px] text-violet-700 hover:bg-violet-100 transition-colors">
                {a}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Composer */}
      <div className="px-3 pb-3 pt-1">
        {(attachments.length > 0 || uploadingFile) && (
          <div className="mb-2 flex flex-wrap gap-2" data-testid="assistant-attachments">
            {attachments.map((a) => (
              <AttachmentCard key={a.id} att={a}
                onRemove={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))}
                onPreview={() => setPreviewAtt(a)} />
            ))}
            {uploadingFile && (
              <UploadingCard file={uploadingFile} progress={uploadProgress} />
            )}
          </div>
        )}
        <div className="flex items-end gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 focus-within:border-violet-400 transition-colors">
          <input ref={fileRef} type="file" className="hidden" data-testid="assistant-file-input"
            accept=".png,.jpg,.jpeg,.webp,.pdf,.zip,.txt,.csv,.md,.json,.log,.xml,.docx,.xlsx,.pptx"
            onChange={(e) => uploadFile(e.target.files?.[0])} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || attachments.length >= 5}
            data-testid="assistant-attach-btn" title="画像 / PDF / Office / ZIP を添付（AIが内容を読み取ります）"
            className="shrink-0 self-center p-1 rounded text-zinc-400 hover:text-violet-600 hover:bg-violet-50 disabled:opacity-40 transition-colors">
            {uploading ? <CircleNotch size={16} className="animate-spin" /> : <Paperclip size={16} />}
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder="質問を入力…（Enterで送信）"
            data-testid="assistant-input"
            className="flex-1 resize-none bg-transparent text-sm outline-none max-h-32 min-h-[24px]"
            style={{ height: 'auto' }}
          />
          {streaming ? (
            <button type="button" onClick={stop} data-testid="assistant-stop-btn"
              className="shrink-0 inline-flex items-center gap-1 px-3 h-8 rounded-lg bg-rose-600 text-white text-xs font-medium hover:bg-rose-700 transition-colors">
              <Stop size={12} weight="fill" /> 停止
            </button>
          ) : (
            <button type="button" onClick={() => send(input, attachedMessage ? [attachedMessage.id] : [])}
              disabled={!input.trim()}
              data-testid="assistant-send-btn"
              className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40 transition-colors">
              <PaperPlaneTilt size={14} weight="fill" />
            </button>
          )}
        </div>
        <p className="mt-1 px-1 text-[10px] text-zinc-400">安全な取消可能アクション（タスク作成・ステータス変更・下書き作成）は5分以内に取消できます — 削除・承認・第三者への通知はできません。</p>
      </div>

      {previewAtt && <AttachmentPreviewModal att={previewAtt} onClose={() => setPreviewAtt(null)} />}

      {actionsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             onClick={() => setActionsOpen(false)} data-testid="assistant-actions-modal">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-zinc-100">
              <h3 className="text-base font-bold text-zinc-800 flex items-center gap-2">
                <Lightning size={16} className="text-amber-500" weight="fill" /> AIアクション履歴
              </h3>
              <p className="mt-1 text-[11px] text-zinc-500">
                コミットから5分以内であれば取り消せます。取り消し後の再実行は影響しません。
              </p>
            </div>
            <div className="p-5 max-h-[60vh] overflow-y-auto space-y-2" data-testid="assistant-actions-list">
              {actionsList.length === 0 ? (
                <p className="text-sm text-zinc-400 text-center py-8">まだアクションは実行されていません</p>
              ) : actionsList.map((a) => {
                const canUndo = a.status === 'committed'
                  && a.undo_by && new Date(a.undo_by).getTime() > Date.now();
                const badge = a.status === 'undone' ? 'bg-zinc-100 text-zinc-500'
                            : a.status === 'expired' ? 'bg-zinc-100 text-zinc-400'
                            : 'bg-emerald-50 text-emerald-700 border border-emerald-200';
                const badgeLbl = a.status === 'undone' ? '取消済' : a.status === 'expired' ? '期限切れ' : 'コミット';
                return (
                  <div key={a.id} className="rounded-lg border border-zinc-200 bg-white p-3 space-y-1"
                       data-testid={`assistant-action-${a.id}`}>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-semibold text-zinc-800">{a.label || a.kind}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${badge}`}>{badgeLbl}</span>
                      <span className="ml-auto text-[10px] text-zinc-400 tabular-nums">{fmtTime(a.created_at)}</span>
                    </div>
                    <div className="text-[11px] text-zinc-600 truncate">
                      {a.kind === 'create_task' && `タスク「${a.result?.title || ''}」 in ${a.result?.project_name || ''}`}
                      {a.kind === 'update_task_status' && `${a.result?.prev_status} → ${a.result?.new_status}`}
                      {a.kind === 'draft_report' && `日報下書き ${a.result?.report_date || ''}`}
                      {a.kind === 'draft_announcement' && `お知らせ下書き「${a.result?.title || ''}」`}
                    </div>
                    {canUndo && (
                      <button onClick={() => undoAction(a.id)}
                        className="text-[11px] font-semibold text-rose-600 hover:text-rose-700"
                        data-testid={`assistant-action-undo-${a.id}`}>
                        取り消し
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-end p-4 border-t border-zinc-100 bg-zinc-50 rounded-b-2xl">
              <button onClick={() => setActionsOpen(false)}
                className="rounded-lg bg-violet-600 text-white px-4 py-1.5 text-xs font-semibold hover:bg-violet-700"
                data-testid="assistant-actions-close">閉じる</button>
            </div>
          </div>
        </div>
      )}

      {analyticsOpen && analytics && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             onClick={() => setAnalyticsOpen(false)} data-testid="assistant-share-analytics-modal">
          <div className="w-full max-w-md rounded-lg bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-zinc-100">
              <h3 className="text-base font-bold text-zinc-800 flex items-center gap-2">
                <ShareNetwork size={16} className="text-violet-500" weight="fill" /> 共有リンクの閲覧統計
              </h3>
            </div>
            <div className="p-5 space-y-3">
              {!analytics.shared ? (
                <p className="text-sm text-zinc-500">この会話はまだ共有されていません。</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-xl border border-violet-200 bg-violet-50 px-3 py-2" data-testid="share-analytics-views">
                      <p className="text-[10px] font-semibold text-violet-500 uppercase tracking-wider">累計閲覧</p>
                      <p className="text-2xl font-bold text-violet-800">{analytics.view_count}</p>
                    </div>
                    <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2" data-testid="share-analytics-uniques">
                      <p className="text-[10px] font-semibold text-sky-500 uppercase tracking-wider">ユニーク</p>
                      <p className="text-2xl font-bold text-sky-800">{analytics.unique_viewers}</p>
                    </div>
                  </div>
                  <div className="rounded-lg bg-zinc-50 border border-zinc-100 p-3 text-[11px] text-zinc-700 space-y-0.5">
                    <p><span className="text-zinc-400">最終閲覧: </span>{analytics.last_viewed_at ? fmtTime(analytics.last_viewed_at) : '—'}
                    {analytics.last_viewer_name ? ` （${analytics.last_viewer_name}）` : ''}</p>
                    <p><span className="text-zinc-400">共有日: </span>{analytics.shared_at ? fmtTime(analytics.shared_at) : '—'}</p>
                    <p><span className="text-zinc-400">有効期限: </span>{analytics.expires_at
                      ? formatDateTime(analytics.expires_at)
                      : '無期限'}</p>
                  </div>
                  {analytics.recent?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-1">直近の閲覧</p>
                      <ul className="text-[11px] text-zinc-700 space-y-1 max-h-40 overflow-y-auto" data-testid="share-analytics-recent">
                        {analytics.recent.map((r, i) => (
                          <li key={i} className="flex items-center gap-2 border-b border-zinc-100 py-1 last:border-0">
                            <span className="flex-1 truncate">{r.viewer_name || '—'}</span>
                            <span className="text-zinc-400 tabular-nums">{fmtTime(r.viewed_at)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center justify-end p-4 border-t border-zinc-100 bg-zinc-50 rounded-b-2xl">
              <button onClick={() => setAnalyticsOpen(false)}
                className="rounded-lg bg-violet-600 text-white px-4 py-1.5 text-xs font-semibold hover:bg-violet-700"
                data-testid="assistant-share-analytics-close">閉じる</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
